import { useEffect, useCallback } from 'react';
import useGameStore from '../stores/gameStore';
import useUIStore from '../stores/uiStore';
import { useWebSocket } from './useWebSocket';

const ESCALATION_DELAY_500_MS = 500;
const ESCALATION_DELAY_3000_MS = 3000;
/** If a WebSocket player-action gets no response, fall back to REST. */
const WEBSOCKET_ACTION_FALLBACK_MS = 8000;

export const useGameState = () => {
  const gameStore = useGameStore();
  const uiStore = useUIStore();
  const websocket = useWebSocket();

  // Start escalation timers (500 ms → disable all; 3 s → problem overlay) and store IDs so they can be cleared
  const startEscalationTimers = useCallback(() => {
    const id500 = window.setTimeout(() => {
      uiStore.setDisableAllInteractions(true);
    }, ESCALATION_DELAY_500_MS);
    const id3000 = window.setTimeout(() => {
      uiStore.setShowProblemOverlay(true);
    }, ESCALATION_DELAY_3000_MS);
    uiStore.setEscalationTimers(id500, id3000);
  }, [uiStore]);

  // Start action-pending state and escalation (e.g. when navigating to character/adventure menu). Call clearActionPending when done.
  const beginPendingAction = useCallback((loadingMessage: string = 'Loading...') => {
    uiStore.setActionPending(true);
    uiStore.setLoading(true, loadingMessage);
    startEscalationTimers();
  }, [uiStore, startEscalationTimers]);

  // Handle option selection with WebSocket integration
  const selectOption = useCallback(async (optionId: string): Promise<boolean> => {
    const { currentSession, currentPlayer } = gameStore;
    
    if (!currentSession || !currentPlayer) {
      uiStore.setError({ code: 'GAME_ERROR', message: 'No active session or player' });
      return false;
    }

    // Prevent another option from being processed before 500ms (visual disable happens after 500ms)
    if (useUIStore.getState().actionPendingSince !== null) {
      return false;
    }

    let usedRestPath = false;
    try {
      uiStore.setActionPending(true);
      uiStore.setLoading(true, 'Processing your choice...');
      startEscalationTimers();

      if (typeof gameStore.flushPendingUserInputs === 'function') {
        await gameStore.flushPendingUserInputs();
      }
      
      // Check if this is a finish or leave option using the options map
      const option = gameStore.optionsMap[optionId];
      const SCRIPT_DELIMITER = '\u0027\u0027\u0027'; // triple single quote
      const targetStr = typeof option?.target === 'string' ? option.target : '';
      const isScriptLikeTarget =
        targetStr.includes(SCRIPT_DELIMITER) ||
        targetStr.includes('getVariable') ||
        targetStr.includes('getAdventureInvite');
      const forceRestSelectOption =
        option?.target === 'RETURN_TO_ADVENTURE' ||
        (targetStr.includes('RETURN_TO_ADVENTURE') && isScriptLikeTarget) ||
        option?.target === 'DESTROY_SESSION' ||
        (targetStr.includes('DESTROY_SESSION') && isScriptLikeTarget) ||
        (targetStr.startsWith('JOIN_ADVENTURE:')) ||
        (targetStr.includes('JOIN_ADVENTURE') && isScriptLikeTarget) ||
        option?.target === 'START_ADVENTURE' ||
        (targetStr.startsWith('START_ADVENTURE:')) ||
        (targetStr.includes('START_ADVENTURE') && isScriptLikeTarget) ||
        option?.target === 'START_OR_RESUME_ADVENTURE' ||
        (targetStr.startsWith('START_OR_RESUME_ADVENTURE:')) ||
        (targetStr.includes('START_OR_RESUME_ADVENTURE') && isScriptLikeTarget) ||
        option?.target === 'LOGGED_IN' ||
        (targetStr.includes('LOGGED_IN') && isScriptLikeTarget);
      const isFinishOption = option?.target === 'FINISH';
      const isLeaveOption = option?.target === 'LEAVE';
      
      if (isFinishOption) {
        await gameStore.selectFinishOption(optionId);
        usedRestPath = true;
        return true;
      }
      if (isLeaveOption) {
        await gameStore.selectLeaveOption(optionId);
        usedRestPath = true;
        return true; // Left adventure
      }
      
      // Send via WebSocket only when joined to the session room; otherwise REST avoids
      // "must join session before performing actions" errors during connect/reconnect.
      const canUseWebSocket =
        websocket.isConnected &&
        gameStore.isSocketSessionJoined &&
        !forceRestSelectOption;

      if (canUseWebSocket) {
        websocket.sendPlayerAction(currentSession.id, currentPlayer.id, optionId);

        const fallbackTimer = window.setTimeout(async () => {
          if (useUIStore.getState().actionPendingSince === null) {
            return;
          }
          try {
            await gameStore.selectOption(optionId, { deliverEffectToasts: true });
          } catch (fallbackError) {
            uiStore.setError(fallbackError as any, 'Failed to process choice');
          } finally {
            useUIStore.getState().clearActionPending();
          }
        }, WEBSOCKET_ACTION_FALLBACK_MS);
        uiStore.setWebsocketFallbackTimer(fallbackTimer);

        return false;
      }
      usedRestPath = true;
      const result = await gameStore.selectOption(optionId, { deliverEffectToasts: true });
      return result;
      
    } catch (error) {
      const apiError = error as { code?: string; message?: string };
      if (apiError?.code === 'SHARED_VARIABLES_ERROR') {
        uiStore.showToast(
          apiError.message || 'Could not save your contribution. Please try again.',
          'error'
        );
      } else if (
        apiError?.code === 'VALIDATION_ERROR' &&
        typeof apiError?.message === 'string' &&
        apiError.message.toLowerCase().includes('guest adventure')
      ) {
        uiStore.showToast(
          apiError.message || 'Could not create the guest adventure invite. Please try again.',
          'error'
        );
      }
      uiStore.setError(error as any, 'Failed to process choice');
      usedRestPath = true; // Clear on error so user can retry
      throw error;
    } finally {
      if (usedRestPath) {
        uiStore.clearActionPending();
      }
    }
  }, [gameStore, uiStore, websocket, startEscalationTimers]);

  // Handle FinishOption selection
  const selectFinishOption = useCallback(async () => {
    const { currentSession, currentPlayer } = gameStore;
    
    if (!currentSession || !currentPlayer) {
      uiStore.setError({ code: 'GAME_ERROR', message: 'No active session or player' });
      return;
    }

    if (useUIStore.getState().actionPendingSince !== null) {
      return;
    }

    try {
      uiStore.setActionPending(true);
      uiStore.setLoading(true, 'Completing adventure...');
      startEscalationTimers();
      
      await gameStore.selectFinishOption();
      
      uiStore.showToast('Adventure completed successfully!', 'success');
      
    } catch (error) {
      uiStore.setError(error as any, 'Failed to complete adventure');
      throw error;
    } finally {
      uiStore.clearActionPending();
    }
  }, [gameStore, uiStore, startEscalationTimers]);


  // Join session with WebSocket integration
  const joinSession = useCallback(async (sessionId: string) => {
    try {
      uiStore.setLoading(true, 'Joining session...');
      
      // Join via REST API first
      await gameStore.joinSession(sessionId);
      
      // WebSocket will auto-connect via useWebSocket hook
      // No need to manually call websocket.joinSession here
    } catch (error) {
      uiStore.setError(error as any, 'Failed to join session');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Leave session with cleanup
  const leaveSession = useCallback(() => {
    const { currentSession, currentPlayer } = gameStore;
    
    if (currentSession && currentPlayer) {
      websocket.leaveSession(currentSession.id, currentPlayer.id);
    }
    
    gameStore.leaveSession();
    websocket.disconnect();
    
    uiStore.showToast('Left session successfully', 'info');
  }, [gameStore, uiStore, websocket]);

  // Create session
  const createSession = useCallback(async (adventureId: string) => {
    try {
      uiStore.setLoading(true, 'Creating session...');
      
      const sessionId = await gameStore.createSession(adventureId);
      
      uiStore.showToast('Session created successfully!', 'success');
      return sessionId;
    } catch (error) {
      uiStore.setError(error as any, 'Failed to create session');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Start session
  const startSession = useCallback(async () => {
    try {
      uiStore.setLoading(true, 'Starting session...');
      
      await gameStore.startSession();
      
      uiStore.showToast('Session started!', 'success');
    } catch (error) {
      uiStore.setError(error as any, 'Failed to start session');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Load adventure
  const loadAdventure = useCallback(async (adventureId: string) => {
    try {
      uiStore.setLoading(true, 'Loading adventure...');
      
      await gameStore.loadAdventure(adventureId);
      
      uiStore.showToast('Adventure loaded successfully!', 'success');
    } catch (error) {
      uiStore.setError(error as any, 'Failed to load adventure');
      throw error;
    } finally {
      uiStore.setLoading(false);
    }
  }, [gameStore, uiStore]);

  // Update player state
  const updatePlayerState = useCallback((updates: any) => {
    gameStore.updatePlayerState(updates);
  }, [gameStore]);

  // Handle waiting conditions
  const handleWaitingCondition = useCallback((playerId: string, optionId: string) => {
    const waitingCondition = gameStore.waitingConditions.find(
      (condition: any) => condition.playerId === playerId && condition.optionId === optionId
    );
    
    if (waitingCondition) {
      uiStore.showToast(waitingCondition.message, 'warning');
    }
  }, [gameStore.waitingConditions, uiStore]);

  // Clear waiting condition
  const clearWaitingCondition = useCallback((playerId: string, optionId: string) => {
    gameStore.removeWaitingCondition(playerId, optionId);
  }, [gameStore]);

  // Check if option is available (not waiting)
  const isOptionAvailable = useCallback((optionId: string) => {
    const { currentPlayer } = gameStore;
    
    if (!currentPlayer) return false;
    
    return !gameStore.waitingConditions.some(
      (condition: any) => condition.playerId === currentPlayer.id && condition.optionId === optionId
    );
  }, [gameStore]);

  // Get player status
  const getPlayerStatus = useCallback((playerId: string) => {
    const player = gameStore.playerStates[playerId];
    const waitingCondition = gameStore.waitingConditions.find(
      (condition: any) => condition.playerId === playerId
    );
    
    return {
      player,
      isOnline: websocket.isConnected,
      isWaiting: !!waitingCondition,
      waitingMessage: waitingCondition?.message,
    };
  }, [gameStore.playerStates, gameStore.waitingConditions, websocket.isConnected]);

  // Auto-refresh game state when WebSocket connects (e.g. after reconnect or initial guest connect).
  // Skip if we already have a scene for this session to avoid duplicate load when createGuestSession
  // already loaded the scene before navigating to the scene page.
  useEffect(() => {
    if (!websocket.isConnected || !gameStore.currentSession || !gameStore.currentPlayer) {
      return;
    }
    if (gameStore.currentScene) {
      return; // Scene already loaded (e.g. by createGuestSession); avoid duplicate load
    }
    gameStore.loadCurrentScene();
  }, [websocket.isConnected, gameStore.currentSession?.id, gameStore.currentPlayer?.id, gameStore.currentScene?.id]);

  // Clear action pending and escalation timers on unmount so timers never fire after navigate away
  useEffect(() => {
    return () => {
      useUIStore.getState().clearActionPending();
    };
  }, []);

  return {
    // State
    currentSession: gameStore.currentSession,
    currentPlayer: gameStore.currentPlayer,
    currentAdventure: gameStore.currentAdventure,
    adventureActivities: gameStore.adventureActivities,
    adventureActivityTypes: gameStore.adventureActivityTypes,
    currentScene: gameStore.currentScene,
    availableOptions: gameStore.currentScene ? 
      gameStore.currentScene.sections.flatMap(section => 
        section.options.filter(option => gameStore.availableOptionIds.includes(option.id))
      ) : [],
    availableOptionsBySection: gameStore.currentScene ? 
      gameStore.currentScene.sections.map((section, sectionIndex) => ({
        sectionIndex,
        options: section.options.filter(option => gameStore.availableOptionIds.includes(option.id))
      })) : [],
    waitingOptions: gameStore.currentScene ? 
      gameStore.currentScene.sections.flatMap(section => 
        section.options.filter(option => gameStore.waitingOptionIds.includes(option.id))
      ) : [],
    optionsMap: gameStore.optionsMap,
    players: gameStore.players,
    playerStates: gameStore.playerStates,
    waitingConditions: gameStore.waitingConditions,
    isConnected: gameStore.isConnected,
    isLoading: gameStore.isLoading,
    error: gameStore.error,
    // Action pending and escalation (for slow-response UI)
    isActionPending: uiStore.actionPendingSince !== null,
    disableAllInteractions: uiStore.disableAllInteractions,
    showProblemOverlay: uiStore.showProblemOverlay,
    
    // Actions
    selectOption,
    selectFinishOption,
    beginPendingAction,
    joinSession,
    leaveSession,
    createSession,
    startSession,
    loadAdventure,
    loadCurrentScene: gameStore.loadCurrentScene,
    updatePlayerState,
    handleWaitingCondition,
    clearWaitingCondition,
    isOptionAvailable,
    getPlayerStatus,
    clearError: gameStore.clearError,
    clearSessionState: gameStore.clearSessionState,
    
    // WebSocket integration
    websocket,
  };
};

export default useGameState;
